import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { PaperClipIcon, PaperAirplaneIcon, ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { portalGetThreads, portalGetThread, portalReply, portalGetAttachmentUrl } from '../../services/messagesService';
import api from '../../services/api';
import Spinner from '../../components/common/Spinner';

async function downloadAttachment(url, fileName) {
  try {
    const res = await api.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(blobUrl);
  } catch {
    toast.error('Failed to download file');
  }
}

const fmtTime = (ts) => {
  if (!ts) return '';
  const d = parseISO(ts);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60 * 60 * 24) return format(d, 'h:mm a');
  if (diff < 60 * 60 * 24 * 7) return format(d, 'EEE');
  return format(d, 'MMM d');
};

function MessageBubble({ msg, isOurs, threadId }) {
  const isClient = msg.senderRole === 'client';
  const senderName = isClient
    ? 'You'
    : `${msg.sender?.firstName || ''} ${msg.sender?.lastName || ''}`.trim() || 'Care team';

  return (
    <div className={`flex ${isOurs ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className="max-w-[80%]">
        <div className={`text-xs text-neutral-400 dark:text-neutral-500 mb-1 ${isOurs ? 'text-right' : ''}`}>
          {senderName} · {format(parseISO(msg.createdAt), 'MMM d, h:mm a')}
        </div>
        <div className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words ${
          isOurs
            ? 'bg-primary-600 text-white rounded-br-sm'
            : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-bl-sm'
        }`}>
          {msg.content !== '(attachment)' && msg.content}
          {(msg.attachments || []).map((att, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => downloadAttachment(portalGetAttachmentUrl(threadId, msg.id, idx), att.fileName)}
              className={`flex items-center gap-1.5 mt-1 text-xs underline ${isOurs ? 'text-primary-100' : 'text-primary-600 dark:text-primary-400'}`}
            >
              <PaperClipIcon className="h-3.5 w-3.5" />
              {att.fileName}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ThreadView({ threadId, onBack }) {
  const queryClient = useQueryClient();
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['portal-message-thread', threadId],
    queryFn: () => portalGetThread(threadId),
    refetchInterval: 15000,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['portal-message-threads'] });
  }, [data, queryClient]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.thread?.messages?.length]);

  const replyMutation = useMutation({
    mutationFn: () => portalReply(threadId, text, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-message-thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['portal-message-threads'] });
      setText('');
      setFile(null);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to send'),
  });

  const sendMessage = () => {
    if (!text.trim() && !file) return;
    replyMutation.mutate();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (isLoading) return <div className="flex justify-center items-center h-64"><Spinner /></div>;

  const thread = data?.thread;
  const messages = thread?.messages || [];

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 160px)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
        <button onClick={onBack} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <p className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">{thread?.subject}</p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 && (
          <p className="text-center text-sm text-neutral-400 dark:text-neutral-500 py-8">No messages yet.</p>
        )}
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            isOurs={msg.senderRole === 'client'}
            threadId={threadId}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 px-4 py-3">
        {file && (
          <div className="flex items-center gap-2 mb-2 text-xs bg-neutral-100 dark:bg-neutral-700 rounded-lg px-3 py-1.5">
            <PaperClipIcon className="h-3.5 w-3.5 text-neutral-500" />
            <span className="flex-1 truncate text-neutral-700 dark:text-neutral-300">{file.name}</span>
            <button onClick={() => setFile(null)} className="text-neutral-400 hover:text-neutral-600">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            className="flex-1 input text-sm resize-none py-2 min-h-[40px] max-h-32"
            rows={1}
            placeholder="Type a message… (Enter to send)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <button
            onClick={() => fileRef.current?.click()}
            className="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
            title="Attach file"
          >
            <PaperClipIcon className="h-5 w-5" />
          </button>
          <button
            onClick={sendMessage}
            disabled={(!text.trim() && !file) || replyMutation.isPending}
            className="p-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PortalMessages() {
  const [activeThreadId, setActiveThreadId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['portal-message-threads'],
    queryFn: portalGetThreads,
    refetchInterval: 30000,
  });

  const threads = data?.threads ?? [];

  if (isLoading) {
    return <div className="flex justify-center py-20"><Spinner /></div>;
  }

  if (activeThreadId) {
    return (
      <div className="max-w-2xl mx-auto px-4">
        <ThreadView threadId={activeThreadId} onBack={() => setActiveThreadId(null)} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">Messages</h1>

      {threads.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-neutral-500 dark:text-neutral-400 text-sm">No messages from your care team yet.</p>
          <p className="text-neutral-400 dark:text-neutral-500 text-xs mt-1">When your provider sends you a message, it will appear here.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-800">
          {threads.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveThreadId(t.id)}
              className="w-full text-left px-4 py-3.5 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors first:rounded-t-xl last:rounded-b-xl"
            >
              <div className="flex items-start justify-between gap-2">
                <span className={`text-sm ${t.unread > 0 ? 'font-semibold text-neutral-900 dark:text-neutral-100' : 'text-neutral-700 dark:text-neutral-300'}`}>
                  {t.subject}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {t.unread > 0 && (
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary-600 text-white text-xs font-medium">{t.unread}</span>
                  )}
                  <span className="text-xs text-neutral-400 dark:text-neutral-500">{fmtTime(t.lastMessageAt)}</span>
                </div>
              </div>
              {t.preview && (
                <p className="text-xs text-neutral-400 dark:text-neutral-500 truncate mt-0.5">{t.preview}</p>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
